import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import i18n from 'i18next';

import Check from '@mui/icons-material/Check';
import Folder from '@mui/icons-material/Folder';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import PlayArrow from '@mui/icons-material/PlayArrow';
import RecordVoiceOver from '@mui/icons-material/RecordVoiceOver';

import { Duration } from 'uikit/duration';
import { Icon } from 'uikit/icons';
import { Label } from 'uikit/labels';
import { Progress } from 'uikit/progress';
import { Tooltip } from 'uikit/tooltips';
import Typo from 'uikit/content/components/typo';

import styles from '../styles/uikitCardsCardMedia.module.scss';

const CardMedia = ({
  children = null,
  image = null,
  type = '',
  variant = '',
  status = '',
  progress = 0,
  duration = 0,
  isComplete = false,
  liveText = '',
  dateMessage = '',
  shouldHideFolderIcon = false,
  className = '',
  testId = '',
}) => {
  const typeOrDefaultTopic = type || (!image && variant === 'list' && 'topic');
  let itemProgress = 0;
  if (!isComplete) {
    if (type === 'video' && Boolean(progress) && duration > 0) {
      itemProgress = (100 * progress) / duration;
    } else if (type === 'upnext' && Boolean(progress)) {
      itemProgress = progress;
    }
  }

  const CLASSES = {
    variant: {
      content: styles.isVariantContent,
      embed: styles.isVariantEmbed,
      grid: styles.isVariantGrid,
      list: styles.isVariantList,
    },
    type: {
      live: styles.isTypeLive,
      topic: styles.isTypeTopic,
      video: styles.isTypeVideo,
    },
    status: {
      completed: styles.isStatusCompleted,
      equivalentPassed: styles.isStatusEquivalentPassed,
      facilitated: styles.isStatusFacilitated,
      inProgress: styles.isStatusInProgress,
      overdue: styles.isStatusOverdue,
      recommended: styles.isStatusRecommended,
      required: styles.isStatusRequired,
    },
  };

  const ICONS = {
    status: {
      completed: <Check />,
      equivalentPassed: <Icon icon="equivalentPassed" />,
      facilitated: <RecordVoiceOver />,
      inProgress: <MoreHoriz />,
      overdue: <Icon icon="required" />,
      recommended: <Icon icon="recommended" />,
      required: <Icon icon="required" />,
    },
  };

  const STATUS_TEXTS = {
    completed: i18n.t('completed', 'Completed'),
    equivalentPassed: i18n.t('equivalent-passed', 'Equivalent Passed'),
    facilitated: i18n.t('facilitated', 'Facilitated'),
    inProgress: i18n.t('in-progress', 'In Progress'),
    overdue: i18n.t('overdue', 'Overdue'),
    recommended: i18n.t('recommended', 'Recommended'),
    required: i18n.t('required', 'Required'),
  };

  const getStatusMarker = () => {
    const statusText = STATUS_TEXTS[status];
    return (
      <Tooltip content={statusText}>
        <div
          className={classnames(
            styles.statusMarker,
            { [CLASSES.status[status]]: status },
            className,
          )}
          data-testid={testId}
        >
          <span className={styles.statusA11yText}>{statusText}</span>
          {ICONS.status[status]}
        </div>
      </Tooltip>
    );
  };

  return (
    <div
      className={classnames(
        styles.element,
        {
          [CLASSES.variant[variant]]: variant,
          [CLASSES.type[typeOrDefaultTopic]]: typeOrDefaultTopic,
        },
        className,
      )}
      data-testid={testId}
    >
      <figure
        className={classnames(styles.figure, {
          [styles[`default-${typeOrDefaultTopic}`]]: !image && typeOrDefaultTopic,
        })}
      >
        {image && <img src={image} alt="" />}
      </figure>

      {status && variant !== 'embed' && getStatusMarker()}

      {type === 'live' && variant === 'grid' && liveText && (
        <Label text={liveText} type="live" className={styles.liveLabel} />
      )}

      {type === 'topic' && !shouldHideFolderIcon && (
        <Folder className={classnames(styles.topicMarker)} />
      )}

      {['video', 'live'].includes(type) && (
        <div className={styles.videoMarker}>
          <PlayArrow />
        </div>
      )}

      {type === 'video' && Boolean(duration) && !status && <Duration duration={duration} />}

      {(itemProgress || dateMessage) && (
        <div className={styles.inProgressContainer}>
          {itemProgress > 0 && (
            <Progress
              value={itemProgress}
              color="primary"
              size="small"
              className={styles.progress}
            />
          )}
          {dateMessage && variant === 'grid' && (
            <div className={styles.dateMessage}>
              <Typo element="div" variant="s01" className={styles.dateMessageText} color="inherit">
                {dateMessage}
              </Typo>
            </div>
          )}
        </div>
      )}
      {children}
    </div>
  );
};

CardMedia.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.oneOf(['content', 'embed', 'grid', 'list']),
  type: PropTypes.oneOf(['course', 'live', 'topic', 'video', 'upnext']),
  image: PropTypes.string,
  status: PropTypes.oneOf([
    'completed',
    'equivalentPassed',
    'facilitated',
    'inProgress',
    'overdue',
    'recommended',
    'required',
    '',
  ]),
  progress: PropTypes.number,
  duration: PropTypes.number,
  isComplete: PropTypes.bool,
  liveText: PropTypes.string,
  dateMessage: PropTypes.string,
  shouldHideFolderIcon: PropTypes.bool,
  className: PropTypes.string,
  testId: PropTypes.string,
};

export { CardMedia as CardMediaStorybook };
export default React.memo(CardMedia);
